import * as React from 'react';

import classNames from 'classnames';
import $ from 'jquery';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import TrackedButton from 'bundles/page/components/TrackedButton';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import 'css!./__styles__/TogglableContent';

const COLLAPSE_PX_RANGE = 40;

const Content = ({ children, forwardedRef, id, tabIndex }: $TSFixMe) => (
  <div className="content-inner" ref={forwardedRef} id={id} tabIndex={tabIndex}>
    {children}
  </div>
);

type Props = {
  className?: string;
  collapsedHeight?: number;
  addCollapseAnimation?: boolean;
  disableCollapseScroll?: boolean;
  isCollapsed?: boolean;
  toggleButtonClassName?: string;
  hideShowLessBtn?: boolean;
  renderToggleButtonContentFunction: (args: { collapsed: boolean }) => React.ReactNode;
  toggleButtonType?: 'button' | 'link';
  trackingName?: string;
  trackingData?: Record<string, unknown>;
  childrenWrapperTag?: string;
  isContentMarkdown?: boolean;
  useSmartCollapse?: boolean;
  label?: string;
  ariaHidden?: boolean;
  onToggleClick?: ({ collapsed }: { collapsed: boolean }) => void;
  id?: string;
  tabIndex?: number;
};

type State = {
  collapsed: boolean;
  alwaysExpand: boolean;
  hasMounted: boolean;
  isAlert: boolean;
};

const DEFAULT_COLLAPSED_HEIGHT = 250;
const DEFAULT_TOGGLE_BUTTON_TYPE = 'button';
const DEFAULT_CHILDREN_WRAPPER_TAG = 'p';
const DEFAULT_IS_CONTENT_MARKDOWN = false;
const DEFAULT_HIDE_SHOW_LESS_BTN = false;

class TogglableContent extends React.Component<React.PropsWithChildren<Props>, State> {
  static contextTypes = {
    getStore: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof TogglableContent.contextTypes>;

  origContentHeight = 0;

  constructor(props: Props, context: $TSFixMe) {
    super(props, context);

    this.state = {
      collapsed: props.isCollapsed ?? true,
      alwaysExpand: false,
      hasMounted: false,
      isAlert: false,
    };

    this.origContentHeight = 0;
  }

  componentDidMount() {
    const alwaysExpand = this.shouldComponentExpand();

    this.setState(() => ({
      hasMounted: true,
      alwaysExpand,
    }));

    window.addEventListener('resize', this.onResize);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { isCollapsed } = this.props;
    const { isCollapsed: nextIsCollapsed = true } = nextProps;

    if (isCollapsed !== nextIsCollapsed) {
      this.setState({
        collapsed: nextIsCollapsed,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    const alwaysExpand = this.shouldComponentExpand();

    this.setState(() => ({
      alwaysExpand,
    }));
  };

  shouldComponentExpand = () => {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'contentInner' does not exist on type 'To... Remove this comment to see the full error message
    const $contentInner = $(this.contentInner);
    const { collapsedHeight = DEFAULT_COLLAPSED_HEIGHT, useSmartCollapse } = this.props;
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'origContentHeight' does not exist on typ... Remove this comment to see the full error message
    this.origContentHeight = $contentInner.height();

    if (useSmartCollapse) {
      return this.origContentHeight <= collapsedHeight + COLLAPSE_PX_RANGE;
    } else {
      return this.origContentHeight <= collapsedHeight;
    }
  };

  splitContent = (content: $TSFixMe) => {
    /* This splits the string into paragraphs using the return `\r` or/and newline `\n` characters */
    const splitContent = content.split(/\r\n|\r|\n/);
    const firstParagraphContent = splitContent[0];
    const restParagraphContent = content.replace(`${firstParagraphContent}`, '').trim();

    return { firstParagraphContent, restParagraphContent };
  };

  toggleCollapse(ev: $TSFixMe) {
    if (ev) {
      ev.preventDefault();
    }

    const {
      disableCollapseScroll,
      addCollapseAnimation,
      collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
      id,
      tabIndex,
    } = this.props;
    const { collapsed } = this.state;

    // @ts-expect-error ts-migrate(2551) FIXME: Property 'content' does not exist on type 'Togglab... Remove this comment to see the full error message
    const $content = $(this.content);

    this.setState({ isAlert: false });

    if (collapsed) {
      // shift focus so screen readers recite the expanded content
      this.setState({ isAlert: true });
      if (id && tabIndex && tabIndex < 0 && typeof document !== undefined) {
        const element = document.getElementById(id);
        element?.setAttribute('tabindex', '0');
        element?.focus({ preventScroll: true });
      }
      $content.animate(
        {
          height: `${collapsed ? this.origContentHeight : collapsedHeight}px`,
        },
        300,
        () => this.setState({ collapsed: !collapsed })
      );
    } else {
      // collapsed content should not be focusable / read by screen readers
      if (id && typeof document !== undefined) {
        document.getElementById(id)?.setAttribute('tabindex', '-1');
      }
      // intended action: To disable scrolling to top
      if (!disableCollapseScroll) {
        // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
        window.scrollTo(0, $content.offset().top);
      }
      // intended action: To add collapsing animation
      if (addCollapseAnimation) {
        $content.animate(
          {
            height: `${collapsed ? this.origContentHeight : collapsedHeight}px`,
          },
          300
        );
      }
      this.setState({ collapsed: !collapsed });
    }
  }

  renderDefaultContentChild = () => {
    const {
      children,
      isContentMarkdown = DEFAULT_IS_CONTENT_MARKDOWN,
      childrenWrapperTag = DEFAULT_CHILDREN_WRAPPER_TAG,
    } = this.props;

    if (isContentMarkdown) {
      return children;
    }

    return React.createElement(childrenWrapperTag, null, children);
  };

  /**
   * This splits the content into two paragraphs so that only the first
   * paragraph is announced when the component is announced
   */
  renderSplitContent = (content: $TSFixMe) => {
    const { id } = this.props;
    const { collapsed, alwaysExpand } = this.state;
    const { firstParagraphContent, restParagraphContent } = this.splitContent(content);

    return (
      <Content
        forwardedRef={(contentInner: $TSFixMe) => {
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'contentInner' does not exist on type 'To... Remove this comment to see the full error message
          this.contentInner = contentInner;
        }}
        id={id}
      >
        <p>{firstParagraphContent}</p>
        {restParagraphContent && <p aria-hidden={collapsed && !alwaysExpand}>{restParagraphContent}</p>}
      </Content>
    );
  };

  renderContent = () => {
    const { children, isContentMarkdown = DEFAULT_IS_CONTENT_MARKDOWN } = this.props;

    if (typeof children === 'string' && !isContentMarkdown) {
      return this.renderSplitContent(children);
    }

    return (
      <Content
        forwardedRef={(contentInner: $TSFixMe) => {
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'contentInner' does not exist on type 'To... Remove this comment to see the full error message
          this.contentInner = contentInner;
        }}
      >
        {this.renderDefaultContentChild()}
      </Content>
    );
  };

  render() {
    const {
      trackingName,
      trackingData,
      hideShowLessBtn = DEFAULT_HIDE_SHOW_LESS_BTN,
      className,
      renderToggleButtonContentFunction,
      toggleButtonClassName,
      toggleButtonType = DEFAULT_TOGGLE_BUTTON_TYPE,
      collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
      onToggleClick,
    } = this.props;
    const { collapsed, alwaysExpand, hasMounted, isAlert } = this.state;
    const { getStore } = this.context;

    const applicationStore = getStore && getStore('ApplicationStore');

    const userAgent = applicationStore && applicationStore.getUserAgent();

    const isSafari = userAgent && userAgent.browser.name === 'Safari';

    const elClassName = classNames('rc-TogglableContent', className, {
      collapsed,
      'is-safari': isSafari,
    });

    const ToggleButtonComp: React.ElementType = toggleButtonType === 'button' ? TrackedButton : TrackedA;

    const handleToggleClick = (ev: $TSFixMe) => {
      onToggleClick?.({ collapsed });
      this.toggleCollapse(ev);
    };
    const toggleButtonProps = Object.assign({}, toggleButtonType === 'link' ? { href: '#' } : {}, {
      className: classNames(
        `toggle-${toggleButtonType}`,
        toggleButtonType === 'link' && 'primary',
        toggleButtonType === 'button' && 'cdp-view-all-button',
        { passive: toggleButtonType === 'button' && 'cdp-view-all-button' }
      ),
      onClick: handleToggleClick,
      trackingName: collapsed ? `expand_${trackingName}` : `collapse_${trackingName}`,
    });

    const contentStyle = {
      height: alwaysExpand || !collapsed ? 'auto' : `${collapsedHeight}px`,
    };

    const renderedButtonContent = renderToggleButtonContentFunction({ collapsed });
    const hideToggleBtnWhenExpanded = hideShowLessBtn && !collapsed;
    const shouldRenderToggleBtn = !hideToggleBtnWhenExpanded && hasMounted && !alwaysExpand;

    return (
      <div className={elClassName}>
        <div
          className="content"
          ref={(content) => {
            // @ts-expect-error ts-migrate(2551) FIXME: Property 'content' does not exist on type 'Togglab... Remove this comment to see the full error message
            this.content = content;
          }}
          style={contentStyle}
          role={isAlert ? 'alert' : undefined}
        >
          {this.renderContent()}
        </div>
        {shouldRenderToggleBtn && (
          <div className="toggle-button-wrapper">
            <ToggleButtonComp
              {...toggleButtonProps}
              aria-hidden={!renderedButtonContent}
              className={toggleButtonClassName}
              tabIndex={!renderedButtonContent ? '-1' : '0'}
              data={trackingData}
            >
              {renderedButtonContent}
            </ToggleButtonComp>
          </div>
        )}
      </div>
    );
  }
}

export default TogglableContent;
